import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { 
  Receipt, 
  RefreshCw, 
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  Building2,
  IndianRupee,
  Calendar,
  CreditCard,
  FileText,
  Edit
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SalesReceiptsPage() {
  const { getAuthHeader } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Allocation dialog state
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [pendingSOs, setPendingSOs] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [allocating, setAllocating] = useState(false);

  // Fetch receipts
  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await axios.get(`${API_URL}/api/sales-receipts`, { 
        params,
        headers: getAuthHeader()
      });
      setReceipts(response.data.receipts || []);
    } catch (error) {
      toast.error('Failed to fetch receipts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [statusFilter]);

  // Filter receipts by search
  const filteredReceipts = useMemo(() => {
    if (!searchTerm) return receipts;
    const term = searchTerm.toLowerCase();
    return receipts.filter(r => 
      r.receipt_number?.toLowerCase().includes(term) ||
      r.client_name?.toLowerCase().includes(term) ||
      r.utr_number?.toLowerCase().includes(term)
    );
  }, [receipts, searchTerm]);

  // Open allocation dialog
  const openAllocationDialog = async (receipt) => {
    try {
      setSelectedReceipt(receipt);
      
      // Fetch pending SOs for this client (backend now includes currently allocated SOs)
      const response = await axios.get(`${API_URL}/api/sales-receipts/${receipt.id}/pending-sos`, {
        headers: getAuthHeader()
      });
      const fetchedSOs = response.data.pending_sos || [];
      setPendingSOs(fetchedSOs);
      
      // Initialize allocations from SOs that are marked as allocated to this receipt
      const existingAllocations = fetchedSOs
        .filter(so => so.is_allocated_to_this_receipt)
        .map(so => ({
          sales_order_id: so.id,
          so_number: so.so_number,
          amount: so.current_allocation_amount || 0,
          selected: true
        }));
      
      setAllocations(existingAllocations);
      
      // Calculate advance amount
      const existingAdvance = (receipt.allocations || [])
        .filter(a => a.allocation_type === 'advance')
        .reduce((sum, a) => sum + a.amount, 0);
      setAdvanceAmount(existingAdvance);
      
      setAllocationDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load allocation data');
      console.error(error);
    }
  };

  // Handle SO selection toggle
  const handleSOToggle = (so, checked) => {
    if (checked) {
      // Calculate effective available amount for this SO
      const effectiveAvailable = so.is_allocated_to_this_receipt 
        ? (so.balance_due || 0) + (so.current_allocation_amount || 0)
        : (so.balance_due || 0);
      
      // Add SO to allocations with default amount = min(effectiveAvailable, remaining)
      setAllocations(prev => [...prev, {
        sales_order_id: so.id,
        so_number: so.so_number,
        amount: Math.min(effectiveAvailable, getRemainingAmount() + (so.current_allocation_amount || 0)),
        selected: true
      }]);
    } else {
      // Remove SO from allocations
      setAllocations(prev => prev.filter(a => a.sales_order_id !== so.id));
    }
  };

  // Handle allocation amount change
  const handleAmountChange = (soId, amount) => {
    setAllocations(prev => prev.map(a => 
      a.sales_order_id === soId ? { ...a, amount: parseFloat(amount) || 0 } : a
    ));
  };

  // Calculate totals
  const getTotalAllocated = () => {
    return allocations.reduce((sum, a) => sum + (a.amount || 0), 0);
  };

  const getRemainingAmount = () => {
    return (selectedReceipt?.amount || 0) - getTotalAllocated();
  };

  // Save allocation
  const handleSaveAllocation = async () => {
    try {
      setAllocating(true);
      
      const remaining = getRemainingAmount();
      
      // Build allocations array
      const allocationPayload = allocations
        .filter(a => a.amount > 0)
        .map(a => ({
          allocation_type: 'so',
          sales_order_id: a.sales_order_id,
          so_number: a.so_number,
          amount: a.amount
        }));
      
      // Add advance allocation if there's remaining amount
      if (remaining > 0.01) {
        allocationPayload.push({
          allocation_type: 'advance',
          sales_order_id: null,
          so_number: null,
          amount: remaining
        });
      }
      
      // Validate total
      const total = allocationPayload.reduce((sum, a) => sum + a.amount, 0);
      if (Math.abs(total - selectedReceipt.amount) > 0.01) {
        toast.error(`Total allocation (${total.toFixed(2)}) must equal receipt amount (${selectedReceipt.amount.toFixed(2)})`);
        return;
      }
      
      await axios.post(`${API_URL}/api/sales-receipts/${selectedReceipt.id}/allocate`, {
        allocations: allocationPayload
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Receipt allocated successfully');
      setAllocationDialogOpen(false);
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate receipt');
      console.error(error);
    } finally {
      setAllocating(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'partially_allocated':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><AlertCircle className="w-3 h-3 mr-1" />Partial</Badge>;
      case 'fully_allocated':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Allocated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if SO is already allocated
  const isSOAllocated = (soId) => {
    return allocations.some(a => a.sales_order_id === soId);
  };

  // Get allocation amount for SO
  const getAllocationAmount = (soId) => {
    const alloc = allocations.find(a => a.sales_order_id === soId);
    return alloc?.amount || 0;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="w-6 h-6" />
              Sales Receipts
            </h1>
            <p className="text-muted-foreground">
              Allocate client payments to Sales Orders
            </p>
          </div>
          <Button onClick={fetchReceipts} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search by receipt number, client, UTR..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="receipt-search"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Receipts</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partially_allocated">Partially Allocated</SelectItem>
                  <SelectItem value="fully_allocated">Fully Allocated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Receipts</p>
                  <p className="text-2xl font-bold">{receipts.length}</p>
                </div>
                <Receipt className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Allocation</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {receipts.filter(r => r.status === 'pending').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Partially Allocated</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {receipts.filter(r => r.status === 'partially_allocated').length}
                  </p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fully Allocated</p>
                  <p className="text-2xl font-bold text-green-600">
                    {receipts.filter(r => r.status === 'fully_allocated').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Receipts Table */}
        <Card>
          <CardHeader>
            <CardTitle>Receipts Register</CardTitle>
            <CardDescription>
              Click on a receipt to allocate to Sales Orders
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No receipts found</p>
                <p className="text-sm">Receipts are created when bank transactions are recorded</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>UTR / Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead className="text-right">Unallocated</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceipts.map(receipt => (
                    <TableRow key={receipt.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {receipt.client_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {receipt.utr_number || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(receipt.amount)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(receipt.allocated_amount)}
                      </TableCell>
                      <TableCell className="text-right text-yellow-600">
                        {formatCurrency(receipt.unallocated_amount)}
                      </TableCell>
                      <TableCell>{getStatusBadge(receipt.status)}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openAllocationDialog(receipt)}
                          data-testid={`allocate-btn-${receipt.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {receipt.status === 'pending' ? 'Allocate' : 'Edit'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Allocation Dialog */}
        <Dialog open={allocationDialogOpen} onOpenChange={setAllocationDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Allocate Receipt: {selectedReceipt?.receipt_number}
              </DialogTitle>
              <DialogDescription>
                Distribute this payment across Sales Orders or mark as Advance
              </DialogDescription>
            </DialogHeader>

            {selectedReceipt && (
              <div className="space-y-6">
                {/* Receipt Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Client</p>
                    <p className="font-medium">{selectedReceipt.client_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">{formatDate(selectedReceipt.receipt_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">UTR</p>
                    <p className="font-medium">{selectedReceipt.utr_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedReceipt.amount)}</p>
                  </div>
                </div>

                {/* Pending SOs */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Pending Sales Orders for {selectedReceipt.client_name}
                  </h4>
                  
                  {pendingSOs.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground border rounded-lg">
                      <p>No pending SOs found for this client</p>
                      <p className="text-sm">Full amount will be marked as Advance</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">Select</TableHead>
                            <TableHead>SO Number</TableHead>
                            <TableHead className="text-right">SO Value</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Pending</TableHead>
                            <TableHead className="text-right">Allocate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingSOs.map(so => {
                            // For currently allocated SOs, show effective available = balance_due + current allocation
                            const effectiveAvailable = so.is_allocated_to_this_receipt 
                              ? (so.balance_due || 0) + (so.current_allocation_amount || 0)
                              : (so.balance_due || 0);
                            
                            return (
                              <TableRow key={so.id} className={so.is_allocated_to_this_receipt ? 'bg-green-50' : ''}>
                                <TableCell>
                                  <Checkbox
                                    checked={isSOAllocated(so.id)}
                                    onCheckedChange={(checked) => handleSOToggle(so, checked)}
                                    data-testid={`so-checkbox-${so.id}`}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">
                                  {so.so_number}
                                  {so.is_allocated_to_this_receipt && (
                                    <Badge variant="outline" className="ml-2 text-xs bg-green-100 text-green-700 border-green-300">
                                      Currently Allocated
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(so.total_amount)}</TableCell>
                                <TableCell className="text-right text-green-600">{formatCurrency(so.total_advance)}</TableCell>
                                <TableCell className="text-right text-yellow-600">{formatCurrency(effectiveAvailable)}</TableCell>
                                <TableCell className="text-right">
                                  {isSOAllocated(so.id) && (
                                    <Input
                                      type="number"
                                      min="0"
                                      max={effectiveAvailable}
                                      step="0.01"
                                      value={getAllocationAmount(so.id)}
                                      onChange={(e) => handleAmountChange(so.id, e.target.value)}
                                      className="w-28 text-right"
                                      data-testid={`so-amount-${so.id}`}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Allocation Summary */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Receipt Amount:</span>
                    <span className="font-bold">{formatCurrency(selectedReceipt.amount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Allocated to SOs:</span>
                    <span className="text-green-600">{formatCurrency(getTotalAllocated())}</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-3">
                    <span className="font-medium">Remaining as Advance:</span>
                    <span className={`font-bold ${getRemainingAmount() > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {formatCurrency(getRemainingAmount())}
                    </span>
                  </div>
                  {getRemainingAmount() < 0 && (
                    <p className="text-red-500 text-sm">
                      ⚠️ Allocation exceeds receipt amount by {formatCurrency(Math.abs(getRemainingAmount()))}
                    </p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAllocationDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveAllocation} 
                disabled={allocating || getRemainingAmount() < 0}
                data-testid="save-allocation-btn"
              >
                {allocating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Allocation'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
